import React, { useEffect, useRef, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { Tablero } from "../../../services/v2/prospects.service";
import { cuando, dinero, errorTexto, SIMBOLO_PLANTEL } from "./salesHelpers";
import TableroVentas, { BitacoraVentas } from "./tableroVentas";
import TableroPromociones from "./tableroPromociones";

interface Props { campusId: number; onSalir: () => void; }

const mesesAtras = (n: number): { v: string; t: string }[] => {
	const out = [];
	const d = new Date();
	for (let i = 0; i < n; i++) {
		const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
		const v = m.getFullYear() + "-" + String(m.getMonth() + 1).padStart(2, "0");
		out.push({ v, t: m.toLocaleDateString("es-MX", { month: "long", year: "numeric" }) });
	}
	return out;
};

// El histórico de ventas empieza en mayo 2025 (informes hechos a mano).
const MESES_DESDE_MAYO_2025 = (() => { const d = new Date(); return (d.getFullYear() - 2025) * 12 + d.getMonth() - 4 + 1; })();

const num = (v: number) => Number(v).toLocaleString("es-MX", { maximumFractionDigits: 1 });

/**
 * Tablero de Ventas (lienzo): matrículas del mes por equipo, asesora y semana, creaciones de hoy y anuncios.
 * Reemplaza el informe anual de ventas hecho a mano. Cuenta lo que entró por solicitud de matrícula aprobada.
 */
const TableroView: React.FC<Props> = ({ campusId, onSalir }) => {
	const http = HttpManager.getInstance();
	const [mes, setMes] = useState<string>(mesesAtras(1)[0].v);
	// Los equipos de ventas venden para todos los planteles del país (Honduras incluye Online): por eso arranca en "Todo el país".
	const [scope, setScope] = useState<"plantel" | "pais">("pais");
	const [t, setT] = useState<Tablero | null>(null);
	const [error, setError] = useState<string | null>(null);
	// Sube al corregir una venta: vuelve a pedir el tablero sin borrar lo que se ve.
	const [vuelta, setVuelta] = useState(0);
	const [vista, setVista] = useState<"resumen" | "ventas" | "cambios" | "promociones">("resumen");
	const sim = SIMBOLO_PLANTEL[campusId] || "";

	// Otro mes o alcance: se limpia la pantalla. Una corrección (vuelta) solo refresca, sin parpadeo.
	const ultimaClave = useRef("");
	useEffect(() => {
		const clave = campusId + "|" + mes + "|" + scope;
		if (ultimaClave.current !== clave) {
			ultimaClave.current = clave;
			setT(null);
		}
		setError(null);
		// Si cambian de mes antes de que llegue la respuesta, la vieja ya no se pinta.
		let vigente = true;
		http.prospectsService.dashboard(campusId, mes, scope === "pais" ? "pais" : null)
			.then((r) => { if (vigente) setT(r.data); }).catch((e) => { if (vigente) setError(errorTexto(e)); });
		return () => { vigente = false; };
	}, [campusId, mes, scope, vuelta]);


	return (
		<div className="tb">
			<div className="tb-cab">
				<div>
					<h2>Tablero de Ventas</h2>
					<span className="apagado">{t ? t.mesTexto : ""}{scope === "pais" ? " · todo el país" : ""}</span>
				</div>
				<div className="tb-herr">
					<select value={scope} onChange={(e) => setScope(e.target.value as any)} aria-label="Alcance">
						<option value="pais">Todo el país</option>
						<option value="plantel">Este plantel</option>
					</select>
					<select value={mes} onChange={(e) => setMes(e.target.value)} aria-label="Mes">
						{mesesAtras(Math.max(12, MESES_DESDE_MAYO_2025)).map((m) => <option key={m.v} value={m.v}>{m.t}</option>)}
					</select>
					<button className="enlace" onClick={onSalir}>Volver a la bandeja</button>
				</div>
			</div>
			<div className="tb-tabs" role="tablist">
				<button role="tab" aria-selected={vista === "resumen"} className={vista === "resumen" ? "activa" : ""} onClick={() => setVista("resumen")}>Resumen</button>
				<button role="tab" aria-selected={vista === "ventas"} className={vista === "ventas" ? "activa" : ""} onClick={() => setVista("ventas")}>
					Ventas del mes{t?.ventas ? <span className="cnt">{t.ventas.length}</span> : null}
				</button>
				<button role="tab" aria-selected={vista === "cambios"} className={vista === "cambios" ? "activa" : ""} onClick={() => setVista("cambios")}>
					Cambios{t?.bitacora ? <span className="cnt">{t.bitacora.length}</span> : null}
				</button>
				<button role="tab" aria-selected={vista === "promociones"} className={vista === "promociones" ? "activa" : ""} onClick={() => setVista("promociones")}>Promociones</button>
			</div>
			{error ? <div className="aviso error">{error}</div> : null}
			{vista === "promociones" ? (
				<div className="tb-cuerpo"><TableroPromociones campusId={campusId} month={mes} scope={scope === "pais" ? "country" : "campus"} /></div>
			) : !t ? <div className="ventas-vacio">Cargando…</div> : vista === "cambios" ? (
				<div className="tb-cuerpo"><BitacoraVentas ventas={t.ventas || []} bitacora={t.bitacora || []} /></div>
			) : vista === "ventas" ? (
				<div className="tb-cuerpo">
					<TableroVentas campusId={campusId} ventas={t.ventas || []} bitacora={t.bitacora || []} puedeEditar={t.puedeEditar} mesCerrado={!!t.mesCerrado}
						onCambio={() => setVuelta((n) => n + 1)} deInforme={t.fuente === "informe"} />
				</div>
			) : (
				<div className="tb-cuerpo">
					{t.fuente && t.fuente !== "wapp" ? (
						<div className="aviso">{t.fuente === "informe"
							? "Según el informe de ventas hecho a mano (todo el país): antes del 24 de septiembre de 2026 no había solicitudes en WAPP."
							: "Del 1 al 23 según el informe de ventas hecho a mano (todo el país); desde el 24 las solicitudes de WAPP."}</div>
					) : null}
					<div className="tb-kpis">
						<div className="tb-kpi"><span>Matrículas</span><b>{num(t.matriculas)}</b></div>
						<div className="tb-kpi"><span>{t.fuente === "informe" ? "Monto (informe)" : "Monto cobrado"}</span><b>{dinero(t.monto, sim)}</b></div>
						<div className="tb-kpi"><span>Creaciones hoy</span><b>{t.creacionesHoy}</b></div>
						<div className="tb-kpi"><span>Falta para meta</span><b>{num(t.faltaMeta)}</b></div>
						<div className="tb-kpi"><span>Sin asesora</span><b className={t.sinAsesora ? "oro" : ""}>{num(t.sinAsesora)}</b></div>
					</div>

					<div className="tb-grid">
						<div className="tarjeta">
							<div className="tarjeta-cab"><h4>Equipos</h4><span className="apagado">meta {t.meta}</span></div>
							<table className="tb-tabla">
								<thead><tr><th>Equipo</th><th>Mat</th><th>Monto</th><th>Avance</th><th>Falta</th></tr></thead>
								<tbody>
									{t.equipos.map((e) => (
										<tr key={e.equipo}><td>{e.equipo}</td><td>{num(e.mat)}</td><td>{dinero(e.monto, sim)}</td>
											<td><div className="tb-barra"><span style={{ width: Math.min(100, e.avance || 0) + "%" }} /></div></td><td>{num(e.falta)}</td></tr>
									))}
									<tr className="total"><td>Total</td><td>{num(t.matriculas)}</td><td>{dinero(t.monto, sim)}</td><td /><td>{num(t.faltaMeta)}</td></tr>
								</tbody>
							</table>
						</div>
						<div className="tarjeta">
							<h4>Por semana</h4>
							<table className="tb-tabla">
								<thead><tr><th>Días</th><th>Mat</th><th>Monto</th></tr></thead>
								<tbody>{t.semanas.map((s) => <tr key={s.semana}><td>{s.semana}</td><td>{num(s.mat)}</td><td>{dinero(s.monto, sim)}</td></tr>)}</tbody>
							</table>
						</div>
						<div className="tarjeta">
							<div className="tarjeta-cab"><h4>Creaciones de hoy</h4><span className="apagado">{t.creacionesHoy}{t.creaciones[0]?.ultima ? " · última " + cuando(t.creaciones[0].ultima) : ""}</span></div>
							{t.creaciones.map((c) => <div key={c.asesora} className="par"><span>{c.asesora}</span><span>{c.n}</span></div>)}
							{!t.creaciones.length ? <span className="apagado">Nadie nuevo todavía hoy.</span> : null}
						</div>
						<div className="tarjeta">
							<h4>Asesoras</h4>
							<table className="tb-tabla">
								<thead><tr><th>Asesora</th><th>Equipo</th><th>Mat</th><th>Monto</th></tr></thead>
								<tbody>{t.asesoras.map((a) => <tr key={a.asesora}><td>{a.asesora}</td><td className="apagado">{a.equipo}</td><td>{num(a.mat)}</td><td>{dinero(a.monto, sim)}</td></tr>)}</tbody>
							</table>
						</div>
						<div className="tarjeta">
							<h4>Anuncios</h4>
							<table className="tb-tabla">
								<thead><tr><th>Red · anuncio</th><th>Leads</th><th>Mat</th><th>Conv.</th></tr></thead>
								<tbody>{t.anuncios.map((a) => <tr key={a.anuncio + a.red}><td>{a.anuncio}<span className="apagado"> · {a.red}</span></td><td>{a.leads}</td><td>{a.mat}</td><td>{a.conv}%</td></tr>)}</tbody>
							</table>
							{!t.anuncios.length ? <span className="apagado">Sin leads con anuncio este mes.</span> : null}
						</div>
						<div className="tarjeta">
							<div className="tarjeta-cab"><h4>Trimestre</h4><span className="apagado">desde {t.trimestre.desde} · {num(t.trimestre.matriculas)} matrículas · {dinero(t.trimestre.monto, sim)}</span></div>
							{t.trimestre.equipos.map((e) => <div key={e.equipo} className="par"><span>{e.equipo}</span><span>{num(e.mat)} <span className="apagado">/ {t.trimestre.metaTrimestre}</span></span></div>)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default TableroView;
